import type { Cents } from "./money";

/**
 * A DEAL (spec §87) is a property being evaluated BEFORE anybody commits to
 * it. It is deliberately light: one row, its assumptions and its capital
 * sources — no floors, rooms, designs, accounting or job number. Fifty deals
 * may be analysed for every three bought, so nothing here creates a Project;
 * only an explicit conversion does, and after that the deal is FROZEN as the
 * record of what was believed at acquisition.
 */

export type DealStatus = "evaluating" | "offer" | "under-contract" | "passed" | "converted";
export const DEAL_STATUSES: { id: DealStatus; label: string }[] = [
  { id: "evaluating", label: "Evaluating" },
  { id: "offer", label: "Offer made" },
  { id: "under-contract", label: "Under contract" },
  { id: "passed", label: "Passed" },
  { id: "converted", label: "Became a project" },
];

export type DealStrategy = "flip" | "rental";

/** Where the preliminary rehab figure came from (§87.8). */
export type RehabSource = "manual" | "sqft" | "profile" | "history" | "project" | "find";
export const REHAB_SOURCE_LABEL: Record<RehabSource, string> = {
  manual: "Entered by hand",
  sqft: "Quick $/sq ft",
  profile: "Saved estimating profile",
  history: "Your past projects",
  project: "Detailed project estimate",
  find: "Find preliminary model",
};

export type CapitalKind = "bank" | "private" | "heloc" | "business-loc" | "seller" | "cash" | "other";
export const CAPITAL_KIND_LABEL: Record<CapitalKind, string> = {
  bank: "Bank loan",
  private: "Private loan",
  heloc: "HELOC",
  "business-loc": "Business line of credit",
  seller: "Seller financing",
  cash: "Cash",
  other: "Other",
};

/**
 * One source of money. FACILITY LIMIT (what a lender would allow) and AMOUNT
 * BORROWED (what the deal actually draws) are separate fields on purpose —
 * interest and the balance follow the amount borrowed, never the limit.
 */
export interface DealCapitalSource {
  id: string;
  kind: CapitalKind;
  name: string;
  /** Facility size / credit limit. Null = unstated. Informational for cash. */
  limitCents: Cents | null;
  /** Amount modelled as borrowed (for cash: the cash you plan to put in). */
  amountCents: Cents;
  ratePctX100: number | null;
  /** Amortization term in months; null = interest only for the whole period. */
  amortizationMonths: number | null;
  /** Months of interest-only payments before amortization starts. */
  interestOnlyMonths: number;
  pointsPctX100: number | null;
  /** What the points are charged on. */
  pointsOn: "amount" | "limit";
  feesCents: Cents;
  /** at-closing: the whole amount is outstanding from day one; spread: drawn in equal monthly steps over the hold. */
  drawTiming: "at-closing" | "spread";
  /** Stays in place as permanent debt when the property is kept (rental analysis). */
  keepAfterRenovation: boolean;
  sortOrder: number;
}

export interface DealAssumptions {
  askingPriceCents: Cents | null;
  buyerClosingCostsCents: Cents | null;
  bedrooms: number | null;
  bathroomsX10: number | null;
  asIsValueCents: Cents | null;
  stabilizedValueCents: Cents | null;
  valuationNotes: string;

  rehabCents: Cents | null;
  rehabSource: RehabSource;
  /** e.g. "$18.50/sq ft × 1,650 sq ft", "3 completed projects, median". */
  rehabProvenance: string;
  contingencyPctX100: number | null;

  holdMonths: number | null;
  taxesAnnualCents: Cents | null;
  insuranceAnnualCents: Cents | null;
  utilitiesMonthlyCents: Cents | null;
  hoaMonthlyCents: Cents | null;
  otherHoldingMonthlyCents: Cents | null;

  salePriceCents: Cents | null;
  commissionPctX100: number | null;
  sellerClosingPctX100: number | null;
  otherDispositionCents: Cents | null;

  monthlyRentCents: Cents | null;
  vacancyPctX100: number | null;
  rentalTaxesAnnualCents: Cents | null;
  rentalInsuranceAnnualCents: Cents | null;
  maintenancePctX100: number | null;
  managementPctX100: number | null;
  rentalHoaMonthlyCents: Cents | null;
  otherOperatingMonthlyCents: Cents | null;

  refinanceLtvPctX100: number | null;
  refinanceRatePctX100: number | null;
  refinanceAmortizationMonths: number | null;
  refinanceCostsCents: Cents | null;

  targetKind: MaoTargetKind;
  targetValue: number | null;
  heuristicPctX100: number;
  /** Spec §88: where a deal found through Find came from — evidence reference, never a figure. */
  findReference: FindReference | null;
}

export interface FindReference {
  propertyId: string;
  provider: string;
  retrievedAt: string;
  confidence: string;
  arvLowCents: Cents | null;
  arvHighCents: Cents | null;
  rentLowCents: Cents | null;
  rentHighCents: Cents | null;
  renovatedCompCount: number;
  rentalCompCount: number;
  excludedCompIds: string[];
  sampleData: boolean;
}

export type MaoTargetKind = "profit" | "roi" | "margin" | "cash-return" | "cash-on-cash";

export const EMPTY_ASSUMPTIONS: DealAssumptions = {
  askingPriceCents: null,
  buyerClosingCostsCents: null,
  bedrooms: null,
  bathroomsX10: null,
  asIsValueCents: null,
  stabilizedValueCents: null,
  valuationNotes: "",
  rehabCents: null,
  rehabSource: "manual",
  rehabProvenance: "",
  contingencyPctX100: null,
  holdMonths: null,
  taxesAnnualCents: null,
  insuranceAnnualCents: null,
  utilitiesMonthlyCents: null,
  hoaMonthlyCents: null,
  otherHoldingMonthlyCents: null,
  salePriceCents: null,
  commissionPctX100: null,
  sellerClosingPctX100: null,
  otherDispositionCents: null,
  monthlyRentCents: null,
  vacancyPctX100: null,
  rentalTaxesAnnualCents: null,
  rentalInsuranceAnnualCents: null,
  maintenancePctX100: null,
  managementPctX100: null,
  rentalHoaMonthlyCents: null,
  otherOperatingMonthlyCents: null,
  refinanceLtvPctX100: null,
  refinanceRatePctX100: null,
  refinanceAmortizationMonths: null,
  refinanceCostsCents: null,
  targetKind: "profit",
  targetValue: null,
  heuristicPctX100: 7000,
  findReference: null,
};

export interface Deal {
  id: string;
  name: string;
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  status: DealStatus;
  strategy: DealStrategy;
  purchasePriceCents: Cents | null;
  arvCents: Cents | null;
  livingAreaSqftX100: number | null;
  notes: string;
  assumptions: DealAssumptions;
  capital: DealCapitalSource[];
  convertedProjectId: string | null;
  convertedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type DealInput = Omit<
  Deal,
  "id" | "capital" | "convertedProjectId" | "convertedAt" | "archivedAt" | "createdAt" | "updatedAt"
>;

/** What the deal said at the moment it became a project — never edited again. */
export interface DealUnderwritingSnapshot {
  dealId: string;
  projectId: string;
  frozenAt: string;
  engineVersion: string;
  deal: Omit<Deal, "capital"> & { capital: DealCapitalSource[] };
}

export function dealDisplayName(deal: Pick<Deal, "name" | "streetAddress">): string {
  return deal.name.trim() || deal.streetAddress.trim() || "Untitled deal";
}

export function validateDealInput(input: DealInput): string[] {
  const problems: string[] = [];
  if (input.streetAddress.trim() === "" && input.name.trim() === "") {
    problems.push("Enter the property address (or a name) so you can find this deal again.");
  }
  const money: [string, number | null][] = [
    ["Purchase price", input.purchasePriceCents],
    ["After-repair value", input.arvCents],
    ["Asking price", input.assumptions.askingPriceCents],
    ["Rehab estimate", input.assumptions.rehabCents],
    ["Expected sale price", input.assumptions.salePriceCents],
    ["Monthly rent", input.assumptions.monthlyRentCents],
  ];
  for (const [label, value] of money) {
    if (value !== null && value < 0) problems.push(`${label} cannot be negative.`);
  }
  const months = input.assumptions.holdMonths;
  if (months !== null && (!Number.isInteger(months) || months < 0 || months > 120)) {
    problems.push("Holding period must be a whole number of months between 0 and 120.");
  }
  const pct: [string, number | null][] = [
    ["Contingency", input.assumptions.contingencyPctX100],
    ["Commission", input.assumptions.commissionPctX100],
    ["Seller closing costs", input.assumptions.sellerClosingPctX100],
    ["Vacancy", input.assumptions.vacancyPctX100],
    ["Maintenance", input.assumptions.maintenancePctX100],
    ["Management", input.assumptions.managementPctX100],
    ["Refinance loan-to-value", input.assumptions.refinanceLtvPctX100],
  ];
  for (const [label, value] of pct) {
    if (value !== null && (value < 0 || value > 10000)) problems.push(`${label} must be between 0% and 100%.`);
  }
  return problems;
}

export function validateCapitalSource(source: Omit<DealCapitalSource, "id" | "sortOrder">): string[] {
  const problems: string[] = [];
  if (source.amountCents < 0) problems.push("The amount cannot be negative.");
  if (source.limitCents !== null && source.limitCents < 0) problems.push("The limit cannot be negative.");
  if (source.kind !== "cash" && source.limitCents !== null && source.amountCents > source.limitCents) {
    problems.push("The amount borrowed is more than the facility limit.");
  }
  if (source.ratePctX100 !== null && (source.ratePctX100 < 0 || source.ratePctX100 > 10000)) {
    problems.push("The interest rate must be between 0% and 100%.");
  }
  if (source.amortizationMonths !== null && (!Number.isInteger(source.amortizationMonths) || source.amortizationMonths <= 0 || source.amortizationMonths > 480)) {
    problems.push("Amortization must be a whole number of months up to 480 (40 years).");
  }
  if (!Number.isInteger(source.interestOnlyMonths) || source.interestOnlyMonths < 0 || source.interestOnlyMonths > 480) {
    problems.push("Interest-only months must be a whole number.");
  }
  return problems;
}
